import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMinus,
  faMoneyBill,
  faPencilAlt,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import React, { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Table } from 'shared/components';
import { Discount } from '../../models/discountsModel';
import { Button } from '@vadiun/react-components';
import { useVerifyAction } from '@vadiun/react-hooks-legacy';
import Dates from './Dates';
import { isDiscountActive, isPastDate } from 'app/discounts/utils/discounts';
interface DiscountsTableProps {
  discounts: Discount[];
  totalItems: number;
  page: number;
  rowsPerPage: number;
  onChangePage: (page: number, rowsPerPage: number) => void;
  onChangeRowsPerPage: (rowsPerPage: number) => void;
  onSearchChange: (search: string | null) => void;
  onSortChange: (direction: string | null, sortBy: string) => void;
  handlePageChange: (currentPage: number) => void;
  handleDelete: (id: number) => void;
  filters: any;
  onFiltersChange: (filters: any) => void;
}
const DiscountsTable: FC<DiscountsTableProps> = ({
  discounts,
  totalItems,
  page,
  rowsPerPage,
  onChangePage,
  onChangeRowsPerPage,
  onSearchChange,
  onSortChange,
  handlePageChange,
  handleDelete,
  filters,
  onFiltersChange
}) => {
  const verifyAction = useVerifyAction();
  const [searchValue, setSearchValue] = useState<string>('');
  const handleSearchChange = (search: string | null) => {
    setSearchValue(search || '');
    onSearchChange(search || null);
  };
  const columns = [
    {
      name: 'isFinished',
      label: 'Finalizado',
      options: {
        filter: true,
        display: false,
        filterOptions: {
          names: ['Si', 'No']
        },
        filterList: !filters.finished
          ? undefined
          : filters.finished === 'true' || filters.finished === 'Si'
          ? ['Si']
          : ['No'],
        customFilterListOptions: {
          render: (v) => `Finalizados: ${v}`
        }
      }
    },
    {
      name: 'name',
      label: 'Nombre',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          const discount = discounts[tableMeta.rowIndex];
          const isActive = isDiscountActive(
            discount.from_date,
            discount.end_date
          );
          return (
            <div className="flex items-center whitespace-nowrap ">
              {value}
              {isActive && (
                <span className="duration-300 relative flex ml-8">
                  <span
                    className="animate-ping absolute inline-flex h-6 w-9  rounded-full opacity-75"
                    style={{
                      backgroundColor: '#00FF10',
                      boxShadow: '0 0 10px rgba(0, 255, 12, 0.8)',
                      left: '9px'
                    }}
                  ></span>
                  <span
                    className="relative inline-flex rounded-full  px-2 py-1.5 text-white font-bold"
                    style={{
                      backgroundColor: '#00C20C',
                      boxShadow: '0 0 10px rgba(0, 255, 12, 0.8)',
                      fontSize: 10
                    }}
                  >
                    ACTUAL
                  </span>
                </span>
              )}
            </div>
          );
        }
      }
    },
    {
      name: 'from_date',
      label: 'Inicio',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return <Dates value={value} />;
        }
      }
    },
    {
      name: 'end_date',
      label: 'Finalización',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return <Dates value={value} />;
        }
      }
    },
    {
      name: 'percentage',
      label: 'Porcentaje',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => {
          return (
            <div className="flex text-blue-600 gap-4 w-full justify-start ">
              <span
                className=" text-xs font-bold bg-primary-50 p-2 rounded-2xl"
                style={{ fontSize: 13 }}
              >
                {value} %
              </span>
            </div>
          );
        }
      }
    },
    {
      name: 'id',
      label: 'Acciones',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (id: number, { rowIndex: index }) => {
          const isPast = isPastDate(discounts[index].end_date);
          return (
            <div key={discounts[index].id} className="flex gap-4">
              {!isPast && (
                <Link
                  to={'/main/inventories/discounts/' + id}
                  className="inline-flex items-center"
                >
                  <FontAwesomeIcon
                    size="lg"
                    color="dodgerblue"
                    icon={faPencilAlt}
                    className=" hover:text-blue-600"
                  />
                </Link>
              )}
              <Button
                onClick={() =>
                  verifyAction({
                    title: '¡Estás por eliminar un descuento!',
                    body: 'Esta acción va a eliminar al descuento. ¿Estás seguro?',
                    onAccept: () => handleDelete(id)
                  })
                }
                shape="circle"
                color="red"
              >
                <FontAwesomeIcon icon={faTrash} />
              </Button>
            </div>
          );
        }
      }
    }
  ];

  const data = discounts.map((x) => ({
    ...x
  }));
  return (
    <div className="w-full">
      <Table
        options={{
          page: page - 1,
          filter: true,
          download: false,
          searchOpen: searchValue !== '',
          searchText: searchValue,
          elevation: 0,
          selectableRows: 'none',
          print: false,
          serverSide: true,
          count: totalItems,
          rowsPerPage: rowsPerPage,
          onChangePage(page: number) {
            onChangePage(page + 1, rowsPerPage);
            handlePageChange(page + 1);
          },
          onChangeRowsPerPage(rowsPerPage: number) {
            onChangeRowsPerPage(rowsPerPage);
          },
          onSearchChange: handleSearchChange,
          onColumnSortChange: (
            changedColumn: string,
            direction: 'asc' | 'desc'
          ) => {
            onSortChange(direction, changedColumn);
          },
          searchProps: {
            placeholder: 'Buscar fecha...'
          },
          onFilterConfirm: (filtersColumns) => {
            const isFinishedFilter = filtersColumns[0];
            const hasFinished =
              isFinishedFilter && isFinishedFilter.length > 0;
            onFiltersChange({
              finished: hasFinished ? isFinishedFilter[0] === 'No' ? 'false' : 'true' : null
            });
          },
          onFilterChipClose: (index, removedFilter, filterList) => {
            if (index === 0) {
              onFiltersChange({
                ...filters,
                finished:
                  filterList[0].length === 0
                    ? null
                    : filterList[0][0] === 'No'
                    ? 'true'
                    : 'false'
              });
            }
          },
          confirmFilters: true,
          customFilterDialogFooter: (
            currentFilterList,
            applyFilters = () => {}
          ) => {
            return (
              <div className="flex items-center justify-end mt-4">
                <Button onClick={() => applyFilters()}>APLICAR</Button>
              </div>
            );
          }
        }}
        title={
          <div className="flex gap-4 justify-start items-center">
            <div className="flex gap-1">
              <FontAwesomeIcon icon={faMinus} size="xs" />
              <FontAwesomeIcon icon={faMoneyBill} size="xs" />
            </div>
            <div>Descuentos</div>
          </div>
        }
        data={data}
        columns={columns}
      />
    </div>
  );
};

export default DiscountsTable;
